import { useEffect, useState, useCallback } from "react";
import {
  getOrganizationTemplatesPaginated,
  OrganizationTemplateItem,
} from "@/api/weave-x";
import { selectUser } from "../features/userinfo/userinfoSlice";
import { useSelector } from "react-redux";

export const useOrganizationTemplates = () => {
  const [templates, setTemplates] = useState<Array<OrganizationTemplateItem>>(
    [],
  );
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextToken, setNextToken] = useState<string>("");
  const clientId = useSelector(selectUser)?.client_id;

  const refetchTemplates = useCallback(async () => {
    if (!clientId) {
      setTemplates([]);
      setError(null);
      setHasMore(false);
      setNextToken("");
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const result = await getOrganizationTemplatesPaginated(clientId, "", 20);
      setTemplates(result.templates);
      setHasMore(result.hasMore || false);
      setNextToken(result.nextToken || "");
    } catch (error) {
      setError(error as Error);
      setTemplates([]);
      setHasMore(false);
      setNextToken("");
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  const loadMoreTemplates = useCallback(async () => {
    if (!clientId || !hasMore || !nextToken || isLoadingMore) {
      return;
    }

    try {
      setIsLoadingMore(true);
      setError(null);
      const result = await getOrganizationTemplatesPaginated(
        clientId,
        nextToken,
        20,
      );
      setTemplates((prev) => [...prev, ...result.templates]);
      setHasMore(result.hasMore || false);
      setNextToken(result.nextToken || "");
    } catch (error) {
      setError(error as Error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [clientId, hasMore, nextToken, isLoadingMore]);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (!clientId) {
        if (isMounted) {
          setTemplates([]);
          setError(null);
          setHasMore(false);
          setNextToken("");
        }
        return;
      }

      try {
        if (isMounted) {
          setIsLoading(true);
          setError(null);
        }
        const result = await getOrganizationTemplatesPaginated(
          clientId,
          "",
          20,
        );
        if (isMounted) {
          setTemplates(result.templates);
          setHasMore(result.hasMore || false);
          setNextToken(result.nextToken || "");
        }
      } catch (error) {
        if (isMounted) {
          setError(error as Error);
          setTemplates([]);
          setHasMore(false);
          setNextToken("");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [clientId]);

  return {
    templates,
    error,
    isLoading,
    isLoadingMore,
    hasMore,
    refetch: refetchTemplates,
    loadMore: loadMoreTemplates,
  };
};
